import React, { useState, useContext, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AppContext } from '../App';
import { useFilters } from '../context/FilterContext';
import { useCachedData } from '../hooks/useCachedData';
import {
  Zap, TrendingUp, TrendingDown, DollarSign, AlertTriangle, CheckCircle2,
  ChevronDown, ChevronRight, Users, Building2, MapPin, ArrowRight,
  Lightbulb, Clock, Shield, UserPlus, UsersRound, Filter, User
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';

const SEVERITY_CONFIG = {
  critical_alert: { color: 'red', label: 'Critical', bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', icon: AlertTriangle },
  cost_driver: { color: 'orange', label: 'Cost Driver', bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', icon: DollarSign },
  efficiency: { color: 'blue', label: 'Efficiency', bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', icon: TrendingUp },
  pattern: { color: 'purple', label: 'Pattern', bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', icon: Clock },
  positive: { color: 'green', label: 'Positive', bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', icon: CheckCircle2 },
  pay_type_concentration: { color: 'amber', label: 'Pay Type Concentration', bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', icon: Lightbulb },
};

// Recommendations whose type starts with one of these are pay-type-aware concentrations.
// They surface as synthetic insights so they appear in Insights & Actions, not just /recommendations.
const PAY_TYPE_REC_TYPES = new Set([
  'PTO_CONCENTRATION',
  'HOLIDAY_ROTATION',
  'OT_CONCENTRATION',
  'PREMIUM_TYPE_CONCENTRATION',
]);

const REC_TYPE_ICON = {
  FLOAT_POOL: UsersRound,
  REBALANCE_WORKLOAD: Users,
  HIRE: UserPlus,
  SCHEDULE_OPTIMIZATION: Clock,
};

const InsightCard = ({ insight, recommendations, onViewEmployee, navigate }) => {
  const [expanded, setExpanded] = useState(false);

  const config = SEVERITY_CONFIG[insight.category] || SEVERITY_CONFIG.efficiency;
  const IconComp = config.icon;

  // Match recommendations by location + department.
  // For synthetic insights (those derived from a pay-type rec), the embedded rec is
  // pre-attached so the card always renders its action plan inline.
  const matchedRecs = useMemo(() => {
    if (insight._derived_from_rec) {
      return [insight._derived_from_rec];
    }
    if (!recommendations?.length) return [];
    return recommendations.filter(rec => {
      const recLoc = rec.details?.location || '';
      const recDept = rec.details?.job_title || rec.details?.department || '';
      const insLoc = insight.location || '';
      const insDept = insight.department || '';
      // Match by location, or by department if no location
      if (insLoc && recLoc && insLoc === recLoc) return true;
      if (insDept && recDept && insDept === recDept && (!insLoc || insLoc === recLoc)) return true;
      return false;
    });
  }, [insight, recommendations]);

  const empMatch = (insight.id || '').match(/employee[_-](?:ot[_-])?(\d+)/i);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`rounded-xl border ${config.border} ${expanded ? config.bg : 'bg-white'} transition-colors`}
      data-testid={`insight-action-card-${insight.id}`}
    >
      {/* Insight Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full text-left p-4 flex items-start gap-3"
        data-testid={`insight-toggle-${insight.id}`}
      >
        <div className={`w-9 h-9 rounded-lg ${config.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
          <IconComp className={`h-4.5 w-4.5 ${config.text}`} size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${config.bg} ${config.text}`}>
              {config.label}
            </span>
            {insight.urgency === 'high' && (
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-600">Urgent</span>
            )}
            {matchedRecs.length > 0 && (
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                {matchedRecs.length} action{matchedRecs.length > 1 ? 's' : ''}
              </span>
            )}
          </div>
          <h3 className="font-semibold text-slate-800 text-sm leading-snug">{insight.title}</h3>
          <p className="text-xs text-slate-500 mt-1 line-clamp-1">{insight.description}</p>
          <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
            {insight.dollar_impact > 0 && (
              <span className="flex items-center gap-1 font-medium text-slate-600">
                <DollarSign size={12} />
                ${Math.round(insight.dollar_impact).toLocaleString()} impact
              </span>
            )}
            {insight.location && <span className="flex items-center gap-1"><MapPin size={11} />{insight.location}</span>}
            {insight.department && <span className="flex items-center gap-1"><Building2 size={11} />{insight.department}</span>}
          </div>
        </div>
        <div className="flex-shrink-0 mt-1">
          {expanded ? <ChevronDown size={18} className="text-slate-400" /> : <ChevronRight size={18} className="text-slate-400" />}
        </div>
      </button>

      {/* Expanded: Actions */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 space-y-3">
              {/* Employee link */}
              {empMatch && (
                <button
                  onClick={() => onViewEmployee(empMatch[1])}
                  className="flex items-center gap-2 text-sm text-blue-600 hover:underline"
                  data-testid={`insight-emp-link-${empMatch[1]}`}
                >
                  <User size={14} /> View Employee Profile
                </button>
              )}

              {/* Matched Recommendations */}
              {matchedRecs.length > 0 ? (
                <div className="space-y-3">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1.5">
                    <Lightbulb size={12} /> Recommended Actions
                  </p>
                  {matchedRecs.map(rec => {
                    const RecIcon = REC_TYPE_ICON[rec.type] || Lightbulb;
                    return (
                      <div key={rec.id} className="bg-white rounded-lg border border-slate-200 p-3.5">
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                            <RecIcon size={16} className="text-emerald-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                                rec.priority === 'HIGH' ? 'bg-red-100 text-red-700' :
                                rec.priority === 'MEDIUM' ? 'bg-amber-100 text-amber-700' :
                                'bg-slate-100 text-slate-600'
                              }`}>{rec.priority}</span>
                              <span className="text-xs text-slate-400">{rec.type.replace(/_/g, ' ')}</span>
                            </div>
                            <p className="text-sm font-medium text-slate-800">{rec.title}</p>
                            <p className="text-xs text-slate-500 mt-1">{rec.action}</p>

                            {/* Financial Impact */}
                            {rec.financial_impact && (
                              <div className="flex items-center gap-4 mt-2.5 text-xs">
                                {rec.financial_impact.projected_savings > 0 && (
                                  <span className="flex items-center gap-1 text-emerald-700 font-medium bg-emerald-50 px-2 py-1 rounded">
                                    <TrendingDown size={12} />
                                    ${Math.round(rec.financial_impact.projected_savings).toLocaleString()}/yr savings
                                  </span>
                                )}
                                {rec.financial_impact.annual_savings > 0 && !rec.financial_impact.projected_savings && (
                                  <span className="flex items-center gap-1 text-emerald-700 font-medium bg-emerald-50 px-2 py-1 rounded">
                                    <TrendingDown size={12} />
                                    ${Math.round(rec.financial_impact.annual_savings).toLocaleString()}/yr savings
                                  </span>
                                )}
                                {rec.financial_impact.payback_period && (
                                  <span className="text-slate-500">Payback: {rec.financial_impact.payback_period}</span>
                                )}
                              </div>
                            )}

                            {/* Employee links for REBALANCE */}
                            {rec.type === 'REBALANCE_WORKLOAD' && rec.details?.from_employee_id && (
                              <div className="flex items-center gap-3 mt-2 text-xs">
                                <button onClick={() => onViewEmployee(rec.details.from_employee_id)} className="text-blue-600 hover:underline">
                                  {rec.details.from_employee} ({rec.details.from_employee_ot}h OT)
                                </button>
                                <ArrowRight size={12} className="text-slate-300" />
                                <button onClick={() => onViewEmployee(rec.details.to_employee_id)} className="text-blue-600 hover:underline">
                                  {rec.details.to_employee} ({rec.details.to_employee_ot}h OT)
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="bg-slate-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-slate-400">No specific actions matched to this insight.</p>
                  <button
                    onClick={() => navigate('/recommendations')}
                    className="text-xs text-blue-600 hover:underline mt-1"
                  >
                    View all recommendations
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const InsightsActions = () => {
  const { API } = useContext(AppContext);
  const { filters } = useFilters();
  const navigate = useNavigate();

  const [insights, setInsights] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [sortBy, setSortBy] = useState('dollar');
  const [filterCategory, setFilterCategory] = useState('all');

  const fetchFn = useCallback(async () => {
    const insightsPromise = axios.post(`${API}/insights`, {
      start_date: filters?.start_date || undefined,
      end_date: filters?.end_date || undefined,
      location: filters?.location || undefined,
      department: filters?.department || undefined,
      sort_by: sortBy
    }).then(res => res.data.insights || []).catch(() => []);

    const recsPromise = axios.post(`${API}/recommendations`, {
      start_date: filters?.start_date || undefined,
      end_date: filters?.end_date || undefined,
      location: filters?.location || undefined,
      department: filters?.department || undefined
    }).then(res => res.data.recommendations || []).catch(() => []);

    const [ins, recs] = await Promise.all([insightsPromise, recsPromise]);
    return { insights: ins, recommendations: recs };
  }, [API, sortBy, filters?.start_date, filters?.end_date, filters?.location, filters?.department]);

  const { data: cachedData, loading } = useCachedData(
    'insights_actions',
    fetchFn,
    [sortBy, filters?.start_date, filters?.end_date, filters?.location, filters?.department]
  );

  // Sync cached data into local state for filtering/sorting
  React.useEffect(() => {
    if (cachedData) {
      setInsights(cachedData.insights || []);
      setRecommendations(cachedData.recommendations || []);
    }
  }, [cachedData]);

  // Get unique categories (insights + synthetic pay-type-concentration cards).
  const categories = useMemo(() => {
    const cats = new Set(insights.map(i => i.category));
    if (recommendations.some(r => PAY_TYPE_REC_TYPES.has(r.type))) {
      cats.add('pay_type_concentration');
    }
    return ['all', ...Array.from(cats)];
  }, [insights, recommendations]);

  // Filter insights
  // Synthesize pay-type-aware recommendations into Insight-shaped objects so they
  // surface in this view alongside organic insights. Driven entirely by the
  // recommendation's `type` field (which itself comes from AppPayType.PremiumFlag).
  const syntheticInsights = useMemo(() => {
    return recommendations
      .filter(r => PAY_TYPE_REC_TYPES.has(r.type))
      .map(r => {
        const d = r.details || {};
        const fi = r.financial_impact || {};
        const payType = d.pay_type || 'Premium Pay';
        const dept = d.department || '';
        const loc = d.location && d.location !== 'All Locations' ? d.location : '';
        const sharePct = d.share_of_org_hours_pct ?? 0;
        return {
          id: r.id,
          category: 'pay_type_concentration',
          title: r.title,
          description: r.action,
          urgency: r.priority === 'HIGH' ? 'high' : r.priority === 'MEDIUM' ? 'medium' : 'low',
          dollar_impact: fi.annual_savings || fi.projected_savings || 0,
          location: loc || null,
          department: dept || null,
          // Tag as derived so InsightCard knows to render the rec inline
          _derived_from_rec: r,
          _pay_type: payType,
          _share_pct: sharePct,
          _multiplier: d.pay_type_multiplier || 1.0,
        };
      });
  }, [recommendations]);

  const filteredInsights = useMemo(() => {
    const combined = [...insights, ...syntheticInsights];
    if (filterCategory === 'all') return combined;
    return combined.filter(i => i.category === filterCategory);
  }, [insights, syntheticInsights, filterCategory]);

  // Summary stats
  const stats = useMemo(() => {
    const totalImpact = insights.reduce((sum, i) => sum + (i.dollar_impact || 0), 0);
    // "Critical" follows the visual badge: any insight categorized as critical_alert.
    const criticalCount = insights.filter(i => i.category === 'critical_alert').length;
    const actionableCount = recommendations.length;
    const totalSavings = recommendations.reduce((sum, r) => {
      const savings = r.financial_impact?.projected_savings || r.financial_impact?.annual_savings || 0;
      return sum + savings;
    }, 0);
    return { totalImpact, criticalCount, actionableCount, totalSavings };
  }, [insights, recommendations]);

  const handleViewEmployee = (empId) => navigate(`/employee/${empId}`);

  if (loading) {
    return (
      <div className="p-6 max-w-5xl mx-auto" data-testid="insights-actions-loading">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-[#1e3a5f] flex items-center gap-3">
            <Zap className="h-8 w-8" />
            Insights & Actions
          </h1>
          <p className="text-muted-foreground mt-1">Loading insights data...</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[1,2,3,4].map(n => (
            <div key={`stat-skel-${n}`} className="bg-white rounded-xl border border-slate-200 p-4 animate-pulse">
              <div className="h-3 w-20 bg-slate-200 rounded mb-2"></div>
              <div className="h-7 w-28 bg-slate-200 rounded"></div>
            </div>
          ))}
        </div>
        <div className="space-y-3">
          {[1,2,3,4,5].map(n => (
            <div key={`card-skel-${n}`} className="bg-white rounded-xl border border-slate-200 p-4 animate-pulse">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 bg-slate-200 rounded-lg flex-shrink-0"></div>
                <div className="flex-1">
                  <div className="h-3 w-24 bg-slate-200 rounded mb-2"></div>
                  <div className="h-4 w-3/4 bg-slate-200 rounded mb-2"></div>
                  <div className="h-3 w-1/2 bg-slate-100 rounded"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="insights-actions-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-[#1e3a5f] flex items-center gap-3">
          <Zap className="h-8 w-8" />
          Insights & Actions
        </h1>
        <p className="text-muted-foreground mt-1">
          AI-detected alerts with recommended actions — click any insight to see what to do about it
        </p>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="text-xs text-slate-500 flex items-center gap-1"><AlertTriangle size={12} /> Total Impact</div>
          <p className="text-xl font-bold text-red-500 mt-1">${Math.round(stats.totalImpact).toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="text-xs text-slate-500 flex items-center gap-1"><Zap size={12} /> Critical Alerts</div>
          <p className="text-xl font-bold text-[#1e3a5f] mt-1">{stats.criticalCount}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="text-xs text-slate-500 flex items-center gap-1"><Lightbulb size={12} /> Actions Available</div>
          <p className="text-xl font-bold text-emerald-600 mt-1">{stats.actionableCount}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="text-xs text-slate-500 flex items-center gap-1"><DollarSign size={12} /> Potential Savings</div>
          <p className="text-xl font-bold text-emerald-600 mt-1">${Math.round(stats.totalSavings).toLocaleString()}</p>
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        {/* Sort */}
        <div className="flex items-center gap-2 text-sm">
          <span className="text-slate-500">Sort:</span>
          {[
            { id: 'dollar', label: 'Impact' },
            { id: 'urgency', label: 'Urgency' },
            { id: 'ease', label: 'Quick Wins' }
          ].map(s => (
            <button
              key={s.id}
              onClick={() => setSortBy(s.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                sortBy === s.id ? 'bg-[#2E5C8A] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
              data-testid={`sort-${s.id}`}
            >
              {s.label}
            </button>
          ))}
        </div>

        <div className="w-px h-6 bg-slate-200" />

        {/* Category filter */}
        <div className="flex items-center gap-2 text-sm flex-wrap">
          <Filter size={14} className="text-slate-400" />
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setFilterCategory(cat)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                filterCategory === cat ? 'bg-[#FF8C42] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
              data-testid={`filter-cat-${cat}`}
            >
              {cat === 'all' ? 'All' : (SEVERITY_CONFIG[cat]?.label || cat.replace(/_/g, ' '))}
            </button>
          ))}
        </div>

        <span className="text-xs text-slate-400 ml-auto">{filteredInsights.length} insights</span>
      </div>

      {/* Insight Cards */}
      <div className="space-y-3">
        {filteredInsights.length > 0 ? (
          filteredInsights.map(insight => (
            <InsightCard
              key={insight.id}
              insight={insight}
              recommendations={recommendations}
              onViewEmployee={handleViewEmployee}
              navigate={navigate}
            />
          ))
        ) : (
          <div className="text-center py-12 bg-slate-50 rounded-xl border border-dashed border-slate-300">
            <Zap className="h-8 w-8 text-slate-300 mx-auto mb-2" />
            <p className="text-slate-500 font-medium">No insights found</p>
            <p className="text-sm text-slate-400 mt-1">Try adjusting your filters or date range</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default InsightsActions;
